'use client';

import { c as _c } from "react/compiler-runtime";
import { useGetCreatorStats } from '@morfi/backend-sdk-v2/client';
import { isMarketTradeable } from '@morfi/contract-sdk';
import { useLocale, useTranslations } from 'next-intl';
import type { ReactNode, RefObject } from 'react';
import { useEffect, useRef, useState } from 'react';
import { UserAvatar } from '@/components/images/user-avatar';
import { AnimatedButton } from '@/components/ui/animated-button';
import { AnimatedNumber } from '@/components/ui/animated-number';
import { AppTooltip } from '@/components/ui/app-tooltip';
import { Icon, type IconSvgElement } from '@/components/ui/icon';
import { Award01Icon, ChartBarLineIcon, ChartCandleIconSolid, CheckmarkCircle02IconSolid, Clock01Icon, DocumentValidationIcon, DollarCircleIcon, DropletIcon, Layers01Icon, Rocket01Icon, Tick02Icon, UserMultipleIcon } from '@/components/ui/icon/glyphs';
import { D6_SCALE, D9_SCALE } from '@/config/solana';
import { defaultTimeZone } from '@/i18n/locales';
import { Link } from '@/i18n/navigation';
import { useFormattedCurrency } from '@/lib/currency';
import { formatPercentValue } from '@/lib/format/format-number';
import { formatRelativeTime } from '@/lib/format-relative-time';
import { useUserProfilePrefetchIntent } from '@/lib/sdk/prefetch';
import { cn } from '@/lib/ui/utils';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface CreatorInfo {
  /** Creator's user id — null for unregistered wallets. */
  userId: string | null;
  /** Creator's wallet pubkey. Stable per-creator identifier — used as the
   *  immutable seed for the avatar pattern when `userId` is null. */
  walletAddress: string;
  displayName: string | null;
  handle: string | null;
  avatarUrl: string | null;
  avatarNsfw: boolean;
  /** Creator's avatar render preference, threaded from the user's
   *  `avatar_preferences.rasterise` so the in-app avatar matches the
   *  user's own profile setting. */
  avatarPreferences: {
    rasterise: boolean;
  };
}
interface MarketInfo {
  claimPool: string;
  totalVolume: string;
  volume24h: string;
  volume7d: string;
  totalReserves: string;
  uniqueTraders: number;
  numOutcomes: number;
  createdAt: string;
  description: string | null;
  /** Topic e.g. "Sports", "Politics". `null` when unset. */
  category?: string | null;
  /** D9-encoded fraction (e.g. `900_000_000` = 90%). */
  consensusThreshold?: string | number | null;
  /** D6-encoded USDC string of the cumulative boost added to the prize pool. */
  boostTotal?: string | null;
  /** Distinct booster wallet count. */
  numBoosters?: number | null;
}
export interface MarketParameters {
  status: string;
  prizePool: string;
}
interface MarketInfoCardProps {
  market: MarketInfo;
  creator: CreatorInfo | null;
  parameters?: MarketParameters;
  className?: string;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function d6ToDisplay(value: string | undefined): number {
  if (value === undefined) return 0;
  const n = Number(value);
  return Number.isNaN(n) ? 0 : n / D6_SCALE;
}
function formatDateShort(isoDate: string, locale: string): string {
  const date = new Date(isoDate);
  return date.toLocaleDateString(locale, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: defaultTimeZone
  });
}
function capitalize(str: string): string {
  if (str.length === 0) return str;
  return str.charAt(0).toUpperCase() + str.slice(1);
}
function parseD6String(value: string): number {
  const n = Number(value);
  return Number.isNaN(n) ? 0 : n / D6_SCALE;
}
function formatCreatorName(creator: CreatorInfo, anonymousLabel: string): string {
  if (creator.displayName !== null && creator.displayName.length > 0) {
    return creator.displayName;
  }
  if (creator.handle !== null && creator.handle.length > 0) {
    return `@${creator.handle}`;
  }
  return anonymousLabel;
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

/** Inset corner radius shared by Details/Creator stat tiles and text panels. */
const INSET_TILE_RADIUS_CLASS = 'rounded-[calc(var(--radius-card)-0.25rem)]';
function StatItem(t0) {
  const $ = _c(28);
  const {
    label,
    value,
    numericValue,
    formatFn,
    valueClassName,
    icon,
    iconClassName,
    leading,
    href,
    linkProps,
    tooltip
  } = t0;
  const t1 = href !== undefined && "cursor-pointer hover:bg-background-tertiary";
  const t2 = href === undefined && tooltip !== undefined && tooltip !== null && "cursor-help hover:bg-background-tertiary";
  let t3;
  if ($[0] !== t1 || $[1] !== t2) {
    t3 = cn("flex min-w-0 items-center gap-3 bg-background-tertiary/50 px-3 py-2.5 transition-colors", INSET_TILE_RADIUS_CLASS, t1, t2);
    $[0] = t1;
    $[1] = t2;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] !== icon || $[4] !== iconClassName || $[5] !== leading) {
    t4 = leading !== undefined ? <span className="shrink-0">{leading}</span> : icon !== undefined ? <Icon aria-hidden={true} icon={icon} className={cn("size-5 shrink-0", iconClassName ?? "text-foreground-tertiary")} /> : null;
    $[3] = icon;
    $[4] = iconClassName;
    $[5] = leading;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== label) {
    t5 = <span className="truncate text-caption text-foreground-secondary">{label}</span>;
    $[7] = label;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== formatFn || $[10] !== numericValue || $[11] !== value || $[12] !== valueClassName) {
    t6 = numericValue !== undefined && formatFn !== undefined ? <AnimatedNumber value={numericValue} format={formatFn} className={cn("truncate text-body font-medium", valueClassName)} /> : <span className={cn("truncate text-body font-medium tabular-nums", valueClassName)}>{value}</span>;
    $[9] = formatFn;
    $[10] = numericValue;
    $[11] = value;
    $[12] = valueClassName;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] !== t5 || $[15] !== t6) {
    t7 = <div className="flex min-w-0 flex-col gap-0.5">{t5}{t6}</div>;
    $[14] = t5;
    $[15] = t6;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  let t8;
  if ($[17] !== t3 || $[18] !== t4 || $[19] !== t7) {
    t8 = <div className={t3}>{t4}{t7}</div>;
    $[17] = t3;
    $[18] = t4;
    $[19] = t7;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  const cell = t8;
  let t9;
  if ($[21] !== cell || $[22] !== href || $[23] !== linkProps) {
    t9 = href !== undefined ? <Link href={href} className="min-w-0" {...linkProps}>{cell}</Link> : cell;
    $[21] = cell;
    $[22] = href;
    $[23] = linkProps;
    $[24] = t9;
  } else {
    t9 = $[24];
  }
  const wrappedCell = t9;
  if (tooltip === undefined || tooltip === null) {
    return wrappedCell;
  }
  let t10;
  if ($[25] !== tooltip || $[26] !== wrappedCell) {
    t10 = <AppTooltip content={tooltip} side="top" align="start">{wrappedCell}</AppTooltip>;
    $[25] = tooltip;
    $[26] = wrappedCell;
    $[27] = t10;
  } else {
    t10 = $[27];
  }
  return t10;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function MarketInfoCard(t0) {
  const $ = _c(134);
  const {
    market,
    creator,
    parameters,
    className
  } = t0;
  const t = useTranslations("market");
  const locale = useLocale();
  const {
    formatBalance,
    formatD6
  } = useFormattedCurrency();
  const [isDescExpanded, setIsDescExpanded] = useState(false);
  const [isDescTruncated, setIsDescTruncated] = useState(false);
  const descRef = useRef(null);
  const descriptionText = market.description !== null && market.description.trim().length > 0 ? market.description : null;
  let t1;
  let t2;
  if ($[0] !== descriptionText) {
    t1 = () => {
      if (descriptionText === null) {
        return;
      }
      const el = descRef.current;
      if (el === null) {
        return;
      }
      setIsDescTruncated(el.scrollHeight > el.clientHeight);
    };
    t2 = [descriptionText];
    $[0] = descriptionText;
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  useEffect(t1, t2);
  const totalReserves = d6ToDisplay(market.totalReserves);
  const statusIconColor = parameters !== undefined && isMarketTradeable(parameters.status.toLowerCase()) ? "text-feedback-success-content" : "text-foreground-tertiary";
  const tChart = useTranslations("chart");
  let t3;
  if ($[3] !== formatBalance || $[4] !== parameters) {
    t3 = parameters !== undefined ? formatBalance(parseD6String(parameters.prizePool)) : null;
    $[3] = formatBalance;
    $[4] = parameters;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const prizePoolValue = t3;
  const consensusThresholdRaw = market.consensusThreshold;
  let t4;
  if ($[6] !== consensusThresholdRaw) {
    t4 = consensusThresholdRaw !== null && consensusThresholdRaw !== undefined ? (() => {
      const n = Number(consensusThresholdRaw);
      if (Number.isNaN(n)) {
        return null;
      }
      return n / D9_SCALE * 100;
    })() : null;
    $[6] = consensusThresholdRaw;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const winThresholdPct = t4;
  const boostTotalRaw = market.boostTotal;
  const boostTotalNum = boostTotalRaw !== null && boostTotalRaw !== undefined ? d6ToDisplay(boostTotalRaw) : null;
  let t5;
  if ($[8] !== className) {
    t5 = cn("flex w-full flex-col gap-4", className);
    $[8] = className;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const t6 = "flex flex-col gap-2";
  let t7;
  if ($[10] !== t) {
    t7 = t("info.details");
    $[10] = t;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] !== t7) {
    t8 = <div className="text-caption font-medium uppercase tracking-wide text-foreground-secondary">{t7}</div>;
    $[12] = t7;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  const t9 = "grid grid-cols-2 gap-2 sm:grid-cols-4";
  let t10;
  if ($[14] !== parameters || $[15] !== statusIconColor || $[16] !== tChart) {
    t10 = parameters !== undefined && <StatItem icon={CheckmarkCircle02IconSolid} iconClassName={statusIconColor} label={tChart("config_status")} value={capitalize(parameters.status)} tooltip={tChart("config_status_tooltip")} />;
    $[14] = parameters;
    $[15] = statusIconColor;
    $[16] = tChart;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  let t11;
  if ($[18] !== market.category || $[19] !== t) {
    t11 = market.category !== null && market.category !== undefined && <StatItem icon={Layers01Icon} label={t("info.category")} value={capitalize(market.category)} tooltip={t("info.tooltip_category")} />;
    $[18] = market.category;
    $[19] = t;
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  let t12;
  if ($[21] !== t) {
    t12 = t("info.outcomes");
    $[21] = t;
    $[22] = t12;
  } else {
    t12 = $[22];
  }
  const t13 = String(market.numOutcomes);
  let t14;
  if ($[23] !== t) {
    t14 = t("info.tooltip_outcomes");
    $[23] = t;
    $[24] = t14;
  } else {
    t14 = $[24];
  }
  let t15;
  if ($[25] !== t12 || $[26] !== t13 || $[27] !== t14) {
    t15 = <StatItem icon={Award01Icon} label={t12} value={t13} tooltip={t14} />;
    $[25] = t12;
    $[26] = t13;
    $[27] = t14;
    $[28] = t15;
  } else {
    t15 = $[28];
  }
  let t16;
  if ($[29] !== prizePoolValue || $[30] !== tChart) {
    t16 = prizePoolValue !== null && <StatItem icon={DollarCircleIcon} label={tChart("config_prize_pool")} value={prizePoolValue} tooltip={tChart("config_prize_pool_tooltip")} />;
    $[29] = prizePoolValue;
    $[30] = tChart;
    $[31] = t16;
  } else {
    t16 = $[31];
  }
  let t17;
  if ($[32] !== t) {
    t17 = t("info.totalVolume");
    $[32] = t;
    $[33] = t17;
  } else {
    t17 = $[33];
  }
  let t18;
  if ($[34] !== formatD6 || $[35] !== market.totalVolume) {
    t18 = formatD6(market.totalVolume);
    $[34] = formatD6;
    $[35] = market.totalVolume;
    $[36] = t18;
  } else {
    t18 = $[36];
  }
  let t19;
  if ($[37] !== t) {
    t19 = t("info.tooltip_total_volume");
    $[37] = t;
    $[38] = t19;
  } else {
    t19 = $[38];
  }
  let t20;
  if ($[39] !== t17 || $[40] !== t18 || $[41] !== t19) {
    t20 = <StatItem icon={ChartBarLineIcon} label={t17} value={t18} tooltip={t19} />;
    $[39] = t17;
    $[40] = t18;
    $[41] = t19;
    $[42] = t20;
  } else {
    t20 = $[42];
  }
  let t21;
  if ($[43] !== t) {
    t21 = t("info.volume24h");
    $[43] = t;
    $[44] = t21;
  } else {
    t21 = $[44];
  }
  let t22;
  if ($[45] !== formatD6 || $[46] !== market.volume24h) {
    t22 = formatD6(market.volume24h);
    $[45] = formatD6;
    $[46] = market.volume24h;
    $[47] = t22;
  } else {
    t22 = $[47];
  }
  let t23;
  if ($[48] !== t) {
    t23 = t("info.tooltip_volume_24h");
    $[48] = t;
    $[49] = t23;
  } else {
    t23 = $[49];
  }
  let t24;
  if ($[50] !== t21 || $[51] !== t22 || $[52] !== t23) {
    t24 = <StatItem icon={ChartBarLineIcon} label={t21} value={t22} tooltip={t23} />;
    $[50] = t21;
    $[51] = t22;
    $[52] = t23;
    $[53] = t24;
  } else {
    t24 = $[53];
  }
  let t25;
  if ($[54] !== t) {
    t25 = t("info.volume7d");
    $[54] = t;
    $[55] = t25;
  } else {
    t25 = $[55];
  }
  let t26;
  if ($[56] !== formatD6 || $[57] !== market.volume7d) {
    t26 = formatD6(market.volume7d);
    $[56] = formatD6;
    $[57] = market.volume7d;
    $[58] = t26;
  } else {
    t26 = $[58];
  }
  let t27;
  if ($[59] !== t) {
    t27 = t("info.tooltip_volume_7d");
    $[59] = t;
    $[60] = t27;
  } else {
    t27 = $[60];
  }
  let t28;
  if ($[61] !== t25 || $[62] !== t26 || $[63] !== t27) {
    t28 = <StatItem icon={ChartCandleIconSolid} label={t25} value={t26} tooltip={t27} />;
    $[61] = t25;
    $[62] = t26;
    $[63] = t27;
    $[64] = t28;
  } else {
    t28 = $[64];
  }
  let t29;
  if ($[65] !== t) {
    t29 = t("info.liquidity");
    $[65] = t;
    $[66] = t29;
  } else {
    t29 = $[66];
  }
  const t30 = formatBalance(totalReserves);
  let t31;
  if ($[67] !== t) {
    t31 = t("info.tooltip_liquidity");
    $[67] = t;
    $[68] = t31;
  } else {
    t31 = $[68];
  }
  let t32;
  if ($[69] !== formatBalance || $[70] !== t29 || $[71] !== t30 || $[72] !== t31 || $[73] !== totalReserves) {
    t32 = <StatItem icon={DropletIcon} label={t29} value={t30} numericValue={totalReserves} formatFn={formatBalance} tooltip={t31} />;
    $[69] = formatBalance;
    $[70] = t29;
    $[71] = t30;
    $[72] = t31;
    $[73] = totalReserves;
    $[74] = t32;
  } else {
    t32 = $[74];
  }
  let t33;
  if ($[75] !== t) {
    t33 = t("info.traders");
    $[75] = t;
    $[76] = t33;
  } else {
    t33 = $[76];
  }
  const t34 = String(market.uniqueTraders);
  const t35 = market.uniqueTraders;
  let t36;
  if ($[77] !== t) {
    t36 = t("info.tooltip_traders");
    $[77] = t;
    $[78] = t36;
  } else {
    t36 = $[78];
  }
  let t37;
  if ($[79] !== market.uniqueTraders || $[80] !== t33 || $[81] !== t34 || $[82] !== t36) {
    t37 = <StatItem icon={UserMultipleIcon} label={t33} value={t34} numericValue={t35} formatFn={_temp} tooltip={t36} />;
    $[79] = market.uniqueTraders;
    $[80] = t33;
    $[81] = t34;
    $[82] = t36;
    $[83] = t37;
  } else {
    t37 = $[83];
  }
  let t38;
  if ($[84] !== t) {
    t38 = t("info.created");
    $[84] = t;
    $[85] = t38;
  } else {
    t38 = $[85];
  }
  let t39;
  if ($[86] !== locale || $[87] !== market.createdAt) {
    t39 = formatDateShort(market.createdAt, locale);
    $[86] = locale;
    $[87] = market.createdAt;
    $[88] = t39;
  } else {
    t39 = $[88];
  }
  let t40;
  if ($[89] !== t) {
    t40 = t("info.tooltip_created");
    $[89] = t;
    $[90] = t40;
  } else {
    t40 = $[90];
  }
  let t41;
  if ($[91] !== t38 || $[92] !== t39 || $[93] !== t40) {
    t41 = <StatItem icon={Clock01Icon} label={t38} value={t39} tooltip={t40} />;
    $[91] = t38;
    $[92] = t39;
    $[93] = t40;
    $[94] = t41;
  } else {
    t41 = $[94];
  }
  let t42;
  if ($[95] !== locale || $[96] !== t || $[97] !== winThresholdPct) {
    t42 = winThresholdPct !== null && <StatItem icon={Tick02Icon} label={t("info.winThreshold")} value={`${formatPercentValue(winThresholdPct, locale)}%`} tooltip={t("info.tooltip_win_threshold")} />;
    $[95] = locale;
    $[96] = t;
    $[97] = winThresholdPct;
    $[98] = t42;
  } else {
    t42 = $[98];
  }
  const t43 = boostTotalNum !== null && <StatItem icon={Rocket01Icon} label={t("info.boostTotal")} value={formatBalance(boostTotalNum)} numericValue={boostTotalNum} formatFn={formatBalance} tooltip={t("info.tooltip_boost_total")} />;
  let t44;
  if ($[99] !== t10 || $[100] !== t11 || $[101] !== t15 || $[102] !== t16 || $[103] !== t20 || $[104] !== t24 || $[105] !== t28 || $[106] !== t32 || $[107] !== t37 || $[108] !== t41 || $[109] !== t42 || $[110] !== t43) {
    t44 = <div className={t9}>{t10}{t11}{t15}{t16}{t20}{t24}{t28}{t32}{t37}{t41}{t42}{t43}</div>;
    $[99] = t10;
    $[100] = t11;
    $[101] = t15;
    $[102] = t16;
    $[103] = t20;
    $[104] = t24;
    $[105] = t28;
    $[106] = t32;
    $[107] = t37;
    $[108] = t41;
    $[109] = t42;
    $[110] = t43;
    $[111] = t44;
  } else {
    t44 = $[111];
  }
  let t45;
  if ($[112] !== t44 || $[113] !== t8) {
    t45 = <section className={t6}>{t8}{t44}</section>;
    $[112] = t44;
    $[113] = t8;
    $[114] = t45;
  } else {
    t45 = $[114];
  }
  let t46;
  if ($[115] !== descriptionText || $[116] !== isDescExpanded || $[117] !== isDescTruncated || $[118] !== t) {
    t46 = descriptionText !== null && <section className="flex flex-col gap-2"><div className="text-caption font-medium uppercase tracking-wide text-foreground-secondary">{t("info.description")}</div><DescriptionDetailCard description={descriptionText} descRef={descRef} isExpanded={isDescExpanded} isTruncated={isDescTruncated} onToggle={() => setIsDescExpanded(_temp2)} showMoreText={t("info.readMore")} showLessText={t("info.showLess")} /></section>;
    $[115] = descriptionText;
    $[116] = isDescExpanded;
    $[117] = isDescTruncated;
    $[118] = t;
    $[119] = t46;
  } else {
    t46 = $[119];
  }
  let t47;
  if ($[120] !== t || $[121] !== tChart || $[122] !== winThresholdPct) {
    t47 = winThresholdPct !== null && <section className="flex flex-col gap-2"><div className="text-caption font-medium uppercase tracking-wide text-foreground-secondary">{t("info.rules")}</div><div className={cn(INSET_TILE_RADIUS_CLASS, "border border-border-secondary bg-background-tertiary/50 px-3 py-2.5")}><p className="text-body text-foreground-primary">{tChart("rules_body", {
            threshold: Math.round(winThresholdPct)
          })}</p></div></section>;
    $[120] = t;
    $[121] = tChart;
    $[122] = winThresholdPct;
    $[123] = t47;
  } else {
    t47 = $[123];
  }
  let t48;
  if ($[124] !== creator || $[125] !== market.createdAt || $[126] !== t) {
    t48 = creator !== null && <section className="flex flex-col gap-2"><div className="text-caption font-medium uppercase tracking-wide text-foreground-secondary">{t("info.creator")}</div><CreatorDetailCard creator={creator} createdAt={market.createdAt} /></section>;
    $[124] = creator;
    $[125] = market.createdAt;
    $[126] = t;
    $[127] = t48;
  } else {
    t48 = $[127];
  }
  let t49;
  if ($[128] !== t45 || $[129] !== t46 || $[130] !== t47 || $[131] !== t48 || $[132] !== t5) {
    t49 = <div className={t5}>{t45}{t46}{t47}{t48}</div>;
    $[128] = t45;
    $[129] = t46;
    $[130] = t47;
    $[131] = t48;
    $[132] = t5;
    $[133] = t49;
  } else {
    t49 = $[133];
  }
  return t49;
}

// ---------------------------------------------------------------------------
// Bottom detail cards
// ---------------------------------------------------------------------------

/**
 * Creator stats — four tiles matching the Details grid: creator (avatar + name),
 * created (relative time), top market, and markets created.
 *
 * The stats aggregate accepts either identifier form: a uuid resolves to
 * the user's creator_profiles ∪ wallet pubkeys server-side, and a wallet
 * address is used as the creator pubkey directly (markets key `creator`
 * by the creating wallet), so unregistered creators get real stats too.
 */
function _temp2(prev) {
  return !prev;
}
function _temp(v) {
  return String(Math.round(v));
}
function CreatorDetailCard(t0) {
  const $ = _c(47);
  const {
    creator,
    createdAt
  } = t0;
  const t = useTranslations("market");
  const tProfile = useTranslations("profile");
  const tTimeIntl = useTranslations("time");
  let t1;
  if ($[0] !== tTimeIntl) {
    t1 = (key, params) => tTimeIntl(key as Parameters<typeof tTimeIntl>[0], params);
    $[0] = tTimeIntl;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const tTime = t1;
  const {
    formatD6
  } = useFormattedCurrency();
  const creatorIdentifier = creator.userId ?? creator.walletAddress;
  const creatorIntent = useUserProfilePrefetchIntent(creatorIdentifier);
  let t2;
  if ($[2] !== creatorIdentifier) {
    t2 = {
      userId: creatorIdentifier
    };
    $[2] = creatorIdentifier;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const creatorStatsQuery = useGetCreatorStats(t2);
  const creatorStats = creatorStatsQuery.data;
  const hasCreatorStats = creatorStats !== undefined && creatorStats !== null && creatorStats.marketsCreated > 0;
  const topMarketUsdcD6 = hasCreatorStats ? creatorStats.topMarketVolumeD6 : null;
  const totalMarketsCreated = hasCreatorStats ? creatorStats.marketsCreated : null;
  let t3;
  if ($[4] !== creator || $[5] !== tProfile) {
    t3 = formatCreatorName(creator, tProfile("anonymousMorfian"));
    $[4] = creator;
    $[5] = tProfile;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const creatorName = t3;
  let t4;
  if ($[7] !== createdAt || $[8] !== tTime) {
    t4 = formatRelativeTime(createdAt, tTime);
    $[7] = createdAt;
    $[8] = tTime;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  const createdRelative = t4;
  let t5;
  if ($[10] !== t) {
    t5 = t("info.creator");
    $[10] = t;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  const t6 = creator.userId ?? creator.walletAddress;
  let t7;
  if ($[12] !== creator.avatarNsfw || $[13] !== creator.avatarPreferences.rasterise || $[14] !== creator.avatarUrl || $[15] !== creator.displayName || $[16] !== t6) {
    t7 = <UserAvatar size="sm" userId={t6} displayName={creator.displayName} avatarUrl={creator.avatarUrl} nsfw={creator.avatarNsfw} rasterise={creator.avatarPreferences.rasterise} />;
    $[12] = creator.avatarNsfw;
    $[13] = creator.avatarPreferences.rasterise;
    $[14] = creator.avatarUrl;
    $[15] = creator.displayName;
    $[16] = t6;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  const t8 = `/portfolio/${creatorIdentifier}`;
  let t9;
  if ($[18] !== creatorIntent || $[19] !== creatorName || $[20] !== t5 || $[21] !== t7 || $[22] !== t8) {
    t9 = <StatItem label={t5} value={creatorName} leading={t7} href={t8} linkProps={creatorIntent} />;
    $[18] = creatorIntent;
    $[19] = creatorName;
    $[20] = t5;
    $[21] = t7;
    $[22] = t8;
    $[23] = t9;
  } else {
    t9 = $[23];
  }
  let t10;
  if ($[24] !== t) {
    t10 = t("info.created");
    $[24] = t;
    $[25] = t10;
  } else {
    t10 = $[25];
  }
  let t11;
  if ($[26] !== createdRelative || $[27] !== t10) {
    t11 = <StatItem label={t10} value={createdRelative} icon={Clock01Icon} />;
    $[26] = createdRelative;
    $[27] = t10;
    $[28] = t11;
  } else {
    t11 = $[28];
  }
  let t12;
  if ($[29] !== t) {
    t12 = t("info.topMarket");
    $[29] = t;
    $[30] = t12;
  } else {
    t12 = $[30];
  }
  const t13 = topMarketUsdcD6 ?? "0";
  let t14;
  if ($[31] !== formatD6 || $[32] !== t13) {
    t14 = formatD6(t13, "compact");
    $[31] = formatD6;
    $[32] = t13;
    $[33] = t14;
  } else {
    t14 = $[33];
  }
  let t15;
  if ($[34] !== t12 || $[35] !== t14) {
    t15 = <StatItem label={t12} value={t14} icon={Award01Icon} />;
    $[34] = t12;
    $[35] = t14;
    $[36] = t15;
  } else {
    t15 = $[36];
  }
  let t16;
  if ($[37] !== t) {
    t16 = t("info.marketsCreated");
    $[37] = t;
    $[38] = t16;
  } else {
    t16 = $[38];
  }
  const t17 = totalMarketsCreated === null ? "0" : String(totalMarketsCreated);
  let t18;
  if ($[39] !== t16 || $[40] !== t17) {
    t18 = <StatItem label={t16} value={t17} icon={DocumentValidationIcon} />;
    $[39] = t16;
    $[40] = t17;
    $[41] = t18;
  } else {
    t18 = $[41];
  }
  let t19;
  if ($[42] !== t11 || $[43] !== t15 || $[44] !== t18 || $[45] !== t9) {
    t19 = <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">{t9}{t11}{t15}{t18}</div>;
    $[42] = t11;
    $[43] = t15;
    $[44] = t18;
    $[45] = t9;
    $[46] = t19;
  } else {
    t19 = $[46];
  }
  return t19;
}

/** Market description detail card — full-width, above the rules card. */
function DescriptionDetailCard(t0) {
  const $ = _c(16);
  const {
    description,
    descRef,
    isExpanded,
    isTruncated,
    onToggle,
    showMoreText,
    showLessText
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = cn(INSET_TILE_RADIUS_CLASS, "flex min-w-0 flex-col gap-1 border border-border-secondary bg-background-tertiary/50 px-3 py-2.5");
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const t2 = !isExpanded && "line-clamp-3";
  let t3;
  if ($[1] !== t2) {
    t3 = cn("text-body text-foreground-primary", t2);
    $[1] = t2;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] !== descRef || $[4] !== description || $[5] !== t3) {
    t4 = <p ref={descRef} className={t3}>{description}</p>;
    $[3] = descRef;
    $[4] = description;
    $[5] = t3;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== isExpanded || $[8] !== isTruncated || $[9] !== onToggle || $[10] !== showLessText || $[11] !== showMoreText) {
    t5 = (isTruncated || isExpanded) && <AnimatedButton variant="tertiary" size="xs" className="self-start px-0 text-foreground-brand hover:bg-transparent hover:underline underline-offset-4" onClick={onToggle}>{isExpanded ? showLessText : showMoreText}</AnimatedButton>;
    $[7] = isExpanded;
    $[8] = isTruncated;
    $[9] = onToggle;
    $[10] = showLessText;
    $[11] = showMoreText;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  let t6;
  if ($[13] !== t4 || $[14] !== t5) {
    t6 = <div className={t1}>{t4}{t5}</div>;
    $[13] = t4;
    $[14] = t5;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  return t6;
}